import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { api, ErroreApi, type VocePersona } from '../../servizi/api.ts'
import { Attesa, Avviso, Intestazione } from '../../componenti/Pezzi.tsx'
import { Bottone, Campo, Etichetta, Modale, Scelta, Spunta } from '../../componenti/base.tsx'
import { useLingue } from '../../componenti/persona-campi.tsx'
import {
  BarraCalendario, Calendario, periodoDellaVista, useStretto, type EventoCalendario, type PuntoVuoto, type RigaCalendario,
  type TonoEvento, type TrattoSede, type Vista,
} from '../../componenti/calendario/Calendario.tsx'
import { giornoInParole, oggiARoma } from '../../componenti/calendario/date.ts'
import { STATI, durataLeggibile, type MediazioniPersona, type StatoMediazione } from '../../servizi/mediazioni.ts'
import { ModuloMediazione, type InizialiMediazione } from '../scheda/Mediazioni.tsx'
import { aiutoRicerca, useTrovati } from '../Registro.tsx'

/**
 * «Calendario delle mediazioni» (557; docs/15-calendari-sedi.md, fase 2): la settimana con una riga per
 * mediatore (o per lingua), il giorno con una colonna per mediatore, il mese, l'elenco da stampare.
 *
 * Chi vede che cosa lo decide l'API (decisione 3): il nome della persona e l'ente arrivano solo a chi ha la
 * scheda nel perimetro, gli altri leggono «Mediazione» con mediatore e lingua; le mediazioni dello stesso
 * mediatore in sedi che non si vedono sono «Occupato (altra sede)». In rosso le sovrapposizioni dello
 * stesso mediatore, in arancione la sede chiusa o fuori orario.
 *
 * Lo spazio vuoto apre «Programma una mediazione» con giorno, ora, mediatore e sede gia' scelti: si cerca
 * la scheda e si continua con il modulo della scheda (percorso MIS.MLC, avvisi «Programma lo stesso»).
 * Per i mediatori senza utenza la riga offre il file .ics della settimana, da consegnare (decisione 9).
 */

type Conflitto = { codice: string; gravita: 'rosso' | 'arancione'; messaggio: string }
type VoceCalendario = {
  id: string; giorno: string; dalle: string; alle: string; durata_minuti: number; stato: StatoMediazione
  mediatore_id: string; mediatore: string; lingua: string | null; lingua_nome: string | null
  sede_id: string | null; sede_nome: string | null; fuori_sede: boolean; serie_id: string | null
  risorsa_id: string | null; risorsa_nome: string | null
  persona: { id: string; nome: string; cognome: string } | null
  ente: { id: string; nome: string; sigla: string | null } | null
  conflitti: Conflitto[]; puo_gestire: boolean
}
type MediatoreRiga = { id: string; nome: string; cognome: string; lingue: string[]; usa_gestionale: boolean }
type RispostaCalendario = {
  mediazioni: VoceCalendario[]
  occupati: { mediatore_id: string; giorno: string; dalle: string; alle: string }[]
  mediatori: MediatoreRiga[]
  sedi: { id: string; nome: string }[]
  enti: { id: string; nome: string; sigla: string | null }[]
  /** Mediatori della sede che non ci lavorano da qualche settimana, fuori dalle righe. */
  mediatori_nascosti: number
  stato_sede: TrattoSede[] | null
  puo_programmare: boolean
}
type Dettaglio = VoceCalendario & {
  luogo?: string | null; nota?: string | null; motivo_annullamento?: string | null; percorso_tipologia?: string | null
  programmata_da: string | null; serie: { quante: number; posizione: number } | null
}

const VISTE: Vista[] = ['giorno', 'settimana', 'mese', 'elenco']
const FILTRI = ['sede', 'mediatore', 'lingua', 'ente', 'righe', 'annullate', 'tutti'] as const

const tonoDi = (v: VoceCalendario): TonoEvento => {
  if (v.stato === 'annullata') return 'annullato'
  if (v.conflitti.some((c) => c.gravita === 'rosso')) return 'rosso'
  if (v.conflitti.some((c) => c.gravita === 'arancione')) return 'arancione'
  return v.stato === 'programmata' ? 'normale' : 'concluso'
}
const dove = (v: { fuori_sede: boolean; sede_nome: string | null; risorsa_nome?: string | null }) =>
  v.fuori_sede ? 'fuori sede' : [v.sede_nome ?? 'sede non indicata', v.risorsa_nome].filter(Boolean).join(', ')
const nomeEnte = (e: { nome: string; sigla: string | null }) => (e.sigla ? `${e.sigla} — ${e.nome}` : e.nome)

export function CalendarioMediazioni() {
  const lingue = useLingue()
  const stretto = useStretto()
  const [parametri, setParametri] = useSearchParams()
  const vista = (VISTE as string[]).includes(parametri.get('vista') ?? '') ? parametri.get('vista') as Vista : 'settimana'
  const giorno = /^\d{4}-\d{2}-\d{2}$/.test(parametri.get('giorno') ?? '') ? parametri.get('giorno')! : oggiARoma()
  const filtro = (k: (typeof FILTRI)[number]) => parametri.get(k) ?? ''
  const perLingua = filtro('righe') === 'lingua'

  const [dati, setDati] = useState<RispostaCalendario | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [esito, setEsito] = useState<string | null>(null)
  const [aperta, setAperta] = useState<string | null>(null)
  const [creazione, setCreazione] = useState<InizialiMediazione | null>(null)
  const [versione, setVersione] = useState(0)

  /** Vista, giorno e filtri stanno nell'indirizzo: si condividono e la notifica di una chiusura ci porta. */
  const cambia = useCallback((valori: Record<string, string>) => {
    const nuovi = new URLSearchParams(parametri)
    for (const [k, v] of Object.entries(valori)) { if (v) nuovi.set(k, v); else nuovi.delete(k) }
    setParametri(nuovi, { replace: true })
  }, [parametri, setParametri])

  const { dal, al } = periodoDellaVista(vista, giorno)
  const richiesta = useMemo(() => {
    const q = new URLSearchParams({ dal, al })
    for (const k of ['sede', 'mediatore', 'lingua', 'ente'] as const) if (filtro(k)) q.set(k, filtro(k))
    if (filtro('annullate')) q.set('anche_annullate', '1')
    if (filtro('tutti')) q.set('tutti', '1')
    return `/mediazioni/calendario?${q}`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dal, al, parametri])
  useEffect(() => {
    let vivo = true
    setErrore(null)
    api.get<RispostaCalendario>(richiesta)
      .then((d) => { if (vivo) setDati(d) })
      .catch((e) => { if (vivo) setErrore(e instanceof ErroreApi ? e.message : 'Non riesco a caricare il calendario.') })
    return () => { vivo = false }
  }, [richiesta, versione])

  const nomeLingua = useCallback((codice: string | null) =>
    (codice ? lingue.find((l) => l.codice === codice)?.nome ?? codice : 'Lingua non indicata'), [lingue])

  const righe: RigaCalendario[] = useMemo(() => {
    if (!dati) return []
    if (perLingua) {
      const codici = [...new Set(dati.mediazioni.map((m) => m.lingua ?? ''))]
      return codici.map((c) => ({ chiave: c || '-', titolo: nomeLingua(c || null) }))
        .sort((a, b) => a.titolo.localeCompare(b.titolo, 'it'))
    }
    return dati.mediatori.map((m) => ({
      chiave: m.id, titolo: `${m.cognome} ${m.nome}`,
      sottotitolo: [m.lingue.map((l) => nomeLingua(l).toLowerCase()).join(', '),
        m.usa_gestionale ? '' : 'non usa il gestionale'].filter(Boolean).join(' · ') || undefined,
    }))
  }, [dati, perLingua, nomeLingua])

  const eventi: EventoCalendario[] = useMemo(() => {
    if (!dati) return []
    const mediazioni = dati.mediazioni.map((m): EventoCalendario => ({
      chiave: m.id, giorno: m.giorno, dalle: m.dalle, alle: m.alle,
      titolo: m.persona ? `${m.persona.cognome} ${m.persona.nome}` : 'Mediazione',
      dettaglio: [perLingua ? m.mediatore : nomeLingua(m.lingua), dove(m), m.ente && nomeEnte(m.ente)]
        .filter(Boolean).join(' · '),
      riga: perLingua ? (m.lingua ?? '-') : m.mediatore_id,
      strato: 'mediazioni', tono: tonoDi(m), avvisi: m.conflitti.map((c) => c.messaggio),
      apri: () => setAperta(m.id),
    }))
    // Gli impegni dei mediatori in sedi che non si vedono: solo nelle righe dei mediatori, cioe' nel giorno e
    // nella settimana; nel mese e nell'elenco da stampare sarebbero rumore.
    const occupati = perLingua || vista === 'mese' || vista === 'elenco' ? [] : dati.occupati.map((o, i): EventoCalendario => ({
      chiave: `occupato-${i}`, giorno: o.giorno, dalle: o.dalle, alle: o.alle, titolo: 'Occupato',
      dettaglio: 'altra sede', riga: o.mediatore_id, occupato: true,
    }))
    return [...mediazioni, ...occupati]
  }, [dati, perLingua, nomeLingua, vista])

  const puoProgrammare = !!dati?.puo_programmare
  const mediatoreDi = (id?: string) => dati?.mediatori.find((m) => m.id === id)
  const programma = (p: Partial<PuntoVuoto>) => {
    setEsito(null)
    const oggi = oggiARoma()
    const m = !perLingua ? mediatoreDi(p.riga) : undefined
    setCreazione({
      data: p.giorno && p.giorno >= oggi ? p.giorno : oggi, ora: p.ora,
      sede_id: filtro('sede') || undefined, lingua: perLingua && p.riga && p.riga !== '-' ? p.riga : filtro('lingua') || undefined,
      mediatore: m ? { id: m.id, nome: m.nome, cognome: m.cognome, usa_gestionale: m.usa_gestionale } : undefined,
    })
  }

  // I mediatori senza utenza: il file della settimana da consegnare, nella loro riga.
  const scaricaSettimana = filtro('mediatore')
    ? `/api/mediazioni/calendario/mediatore.ics?mediatore=${filtro('mediatore')}&dal=${dal}&al=${al}` : null

  return (
    <>
      <Intestazione titolo="Calendario delle mediazioni"
                    sottotitolo={`Le mediazioni delle tue sedi e delle persone che segui.${
                      puoProgrammare ? ' Fai clic su uno spazio vuoto per programmarne una.' : ''}`} />
      {esito && <Avviso tipo="successo">{esito}</Avviso>}

      {(() => {
        const filtri = (
      <div className="calendario-filtri">
        <Scelta etichetta="Sede" valore={filtro('sede')} vuota="Tutte le tue sedi" cambia={(v) => cambia({ sede: v })}
                opzioni={(dati?.sedi ?? []).map((s) => ({ valore: s.id, etichetta: s.nome }))} />
        <Scelta etichetta="Mediatore" valore={filtro('mediatore')} vuota="Tutti" cambia={(v) => cambia({ mediatore: v })}
                opzioni={(dati?.mediatori ?? []).map((m) => ({ valore: m.id, etichetta: `${m.cognome} ${m.nome}` }))} />
        <Scelta etichetta="Lingua" valore={filtro('lingua')} vuota="Tutte" cambia={(v) => cambia({ lingua: v })}
                opzioni={lingue.map((l) => ({ valore: l.codice, etichetta: l.nome }))} />
        <Scelta etichetta="Ente" valore={filtro('ente')} vuota="Tutti" cambia={(v) => cambia({ ente: v })}
                opzioni={(dati?.enti ?? []).map((e) => ({ valore: e.id, etichetta: nomeEnte(e) }))} />
        <Scelta etichetta="Una riga per" valore={perLingua ? 'lingua' : 'mediatore'}
                cambia={(v) => cambia({ righe: v === 'lingua' ? 'lingua' : '' })}
                opzioni={[{ valore: 'mediatore', etichetta: 'Mediatore' }, { valore: 'lingua', etichetta: 'Lingua' }]} />
        <Spunta etichetta="Anche le annullate" valore={!!filtro('annullate')} cambia={(v) => cambia({ annullate: v ? '1' : '' })} />
      </div>
        )
        // Sul telefono i filtri occupano lo schermo: stanno chiusi, con il conto di quelli in uso.
        const inUso = (['sede', 'mediatore', 'lingua', 'ente'] as const).filter((k) => filtro(k)).length
        return stretto
          ? <details className="calendario-filtri-telefono"><summary>Filtri{inUso ? ` (${inUso})` : ''}</summary>{filtri}</details>
          : filtri
      })()}

      <BarraCalendario vista={vista} cambiaVista={(v) => cambia({ vista: v })} giorno={giorno}
                       cambiaGiorno={(g) => cambia({ giorno: g })} viste={VISTE}
                       nuovo={puoProgrammare ? () => programma({ giorno }) : undefined} etichettaNuovo="Programma una mediazione">
        {scaricaSettimana && (
          <a className="bottone bottone-secondario bottone-piccolo" href={scaricaSettimana} download
             title="Il file .ics dei giorni mostrati, da dare al mediatore: senza nomi">
            File .ics del mediatore
          </a>
        )}
      </BarraCalendario>

      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!dati && !errore && <Attesa testo="Caricamento del calendario…" />}
      {dati && (
        <>
          <Calendario vista={vista} giorno={giorno} eventi={eventi} righe={righe} statoSede={dati.stato_sede}
                      etichettaRighe={perLingua ? 'Lingua' : 'Mediatore'} etichettaVuoto="Programma una mediazione"
                      vuoto="Nessuna mediazione." alVuoto={puoProgrammare ? programma : undefined}
                      apriGiorno={(g) => cambia({ vista: 'giorno', giorno: g })} />
          {dati.mediatori_nascosti > 0 && !perLingua && (
            <p className="tenue piccolo riga">
              <span>Altri {dati.mediatori_nascosti} mediatori della sede non ci lavorano da qualche settimana e non sono in riga.</span>
              <Bottone variante="discreto" dimensione="piccolo" onClick={() => cambia({ tutti: '1' })}>Mostra tutti</Bottone>
            </p>
          )}
          {!righe.length && vista === 'settimana' && (
            <p className="tenue piccolo">
              Nessuna mediazione in questi giorni. Scegli una sede per vedere i mediatori che ci lavorano e programmare dallo spazio vuoto.
            </p>
          )}
          <ul className="calendario-legenda">
            <li><span className="calendario-campione calendario-tono-rosso" /> Lo stesso mediatore ha un’altra mediazione</li>
            <li><span className="calendario-campione calendario-tono-arancione" /> Sede chiusa, fuori orario o mediatore non disponibile</li>
            <li><span className="calendario-campione calendario-occupato" /> Occupato in un’altra sede</li>
            {!filtro('sede') && <li>Scegli una sede per vedere le ore in cui è chiusa.</li>}
          </ul>
        </>
      )}

      {aperta && <DettaglioMediazione id={aperta} chiudi={() => setAperta(null)} />}
      {creazione && (
        <ProgrammaDalCalendario iniziali={creazione} chiudi={() => setCreazione(null)}
                                fatto={(testo) => { setCreazione(null); setEsito(testo); setVersione((v) => v + 1) }} />
      )}
    </>
  )
}

// --- dettaglio -------------------------------------------------------------------------------------------

/** Una mediazione del calendario: quello che chi guarda puo' leggere, e il file .ics. */
export function DettaglioMediazione({ id, chiudi }: { id: string; chiudi: () => void }) {
  const [d, setD] = useState<Dettaglio | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  useEffect(() => {
    api.get<Dettaglio>(`/mediazioni/calendario/${id}`)
      .then(setD).catch((e) => setErrore(e instanceof ErroreApi ? e.message : 'Non riesco a caricare la mediazione.'))
  }, [id])

  return (
    <Modale titolo={d?.persona ? `Mediazione con ${d.persona.nome} ${d.persona.cognome}` : 'Mediazione'} chiudi={chiudi}
            azioni={<>
              {d && (
                <a className="bottone bottone-secondario" href={`/api/mediazioni/${id}.ics`} download
                   title="Il file per il calendario del telefono o di Outlook, senza nomi">Scarica il file .ics</a>
              )}
              {d?.persona && <Link className="bottone bottone-primario" to={`/persone/${d.persona.id}`}>Apri la scheda</Link>}
            </>}>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!d && !errore && <Attesa />}
      {d && (
        <>
          {d.conflitti.map((c) => (
            <Avviso key={c.codice} tipo={c.gravita === 'rosso' ? 'errore' : 'attenzione'}>{c.messaggio}</Avviso>
          ))}
          <dl className="calendario-dettaglio">
            <dt>Quando</dt>
            <dd>{giornoInParole(d.giorno, true)}, {d.dalle}-{d.alle} ({durataLeggibile(d.durata_minuti)})</dd>
            <dt>Mediatore</dt><dd>{d.mediatore}</dd>
            <dt>Lingua</dt><dd>{d.lingua_nome ?? 'non indicata'}</dd>
            <dt>Dove</dt><dd>{d.fuori_sede ? `Fuori sede${d.luogo ? `: ${d.luogo}` : ''}` : [d.sede_nome, d.luogo].filter(Boolean).join(' · ') || 'non indicato'}</dd>
            <dt>Stato</dt><dd><Etichetta tono={STATI[d.stato].tono}>{STATI[d.stato].etichetta}</Etichetta></dd>
            {d.serie && <><dt>Ripetizione</dt><dd>La {d.serie.posizione}ª di {d.serie.quante}</dd></>}
            {d.ente && <><dt>Ente</dt><dd>{nomeEnte(d.ente)}</dd></>}
            {d.percorso_tipologia && <><dt>Percorso</dt><dd>{d.percorso_tipologia}</dd></>}
            {d.nota && <><dt>Nota</dt><dd>{d.nota}</dd></>}
            {d.motivo_annullamento && <><dt>Annullata perché</dt><dd>{d.motivo_annullamento}</dd></>}
            {d.programmata_da && <><dt>Programmata da</dt><dd>{d.programmata_da}</dd></>}
          </dl>
          {!d.persona && (
            <p className="tenue piccolo">La persona non è fra quelle che segui: il nome e i dettagli li vede chi ha la scheda.</p>
          )}
          {d.persona && d.puo_gestire && d.stato === 'programmata' && (
            <p className="tenue piccolo">Per spostarla o annullarla apri la scheda, linguetta Mediazioni.</p>
          )}
        </>
      )}
    </Modale>
  )
}

// --- creazione rapida ------------------------------------------------------------------------------------

type SchedaScelta = { id: string; etichetta: string }
const percorsoPersone = (termine: string) => `/persone?cerca=${encodeURIComponent(termine)}&limite=10`
const schedeProgrammabili = (r: { risultati: VocePersona[] }): SchedaScelta[] =>
  r.risultati.filter((p) => !p.mascherata).map((p) => ({ id: p.id, etichetta: `${p.etichetta} · scheda n. ${p.numero_scheda}` }))

/**
 * Dallo spazio vuoto: prima la scheda, poi il modulo della scheda con giorno, ora, mediatore e sede
 * proposti. Il percorso di mediazione (MIS.MLC) si sceglie o si apre come nella scheda.
 */
function ProgrammaDalCalendario({ iniziali, chiudi, fatto }: {
  iniziali: InizialiMediazione; chiudi: () => void; fatto: (testo: string) => void
}) {
  const [testo, setTesto] = useState('')
  const [scelta, setScelta] = useState<SchedaScelta | null>(null)
  const [dati, setDati] = useState<MediazioniPersona | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const ricerca = useTrovati<{ risultati: VocePersona[] }, SchedaScelta>(testo, percorsoPersone, schedeProgrammabili, 350)

  const carica = useCallback(async (s: SchedaScelta) => {
    setErrore(null)
    try {
      const d = await api.get<MediazioniPersona>(`/persone/${s.id}/mediazioni`)
      if (!d.puo_programmare) {
        setErrore('Per questa persona non puoi programmare mediazioni: chiedi a chi la segue.')
        return
      }
      setScelta(s); setDati(d)
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Non riesco a leggere le mediazioni della persona.')
    }
  }, [])

  if (scelta && dati) {
    const quando = [iniziali.data && giornoInParole(iniziali.data), iniziali.ora && `alle ${iniziali.ora}`].filter(Boolean).join(' ')
    return (
      <ModuloMediazione personaId={scelta.id} sedePersona={dati.percorso_nuovo?.sede_id ?? null} dati={dati}
                        iniziali={iniziali} chiudi={chiudi} fatto={fatto}
                        percorsoAperto={async () => { await carica(scelta) }}
                        intestazione={(
                          <p className="riga">
                            <span>Per <strong>{scelta.etichetta}</strong>{quando ? `, ${quando}` : ''}.</span>
                            <Bottone variante="discreto" dimensione="piccolo"
                                     onClick={() => { setScelta(null); setDati(null) }}>Cambia persona</Bottone>
                          </p>
                        )} />
    )
  }
  return (
    <Modale titolo="Programma una mediazione" chiudi={chiudi}
            azioni={<Bottone variante="discreto" onClick={chiudi}>Lascia stare</Bottone>}>
      <p>
        {iniziali.data ? `${giornoInParole(iniziali.data, true)}${iniziali.ora ? ` alle ${iniziali.ora}` : ''}` : 'Nuova mediazione'}
        {iniziali.mediatore ? ` con ${iniziali.mediatore.nome} ${iniziali.mediatore.cognome}` : ''}. Per chi è?
      </p>
      <Campo etichetta="Cerca la scheda" tipo="search" valore={testo} cambia={setTesto}
             segnaposto="Nome, cognome o numero di scheda"
             aiuto={aiutoRicerca(ricerca, 'Nessuna scheda trovata fra quelle che puoi vedere.',
               'Le schede delle persone che segui: le altre non compaiono.')} />
      {ricerca.trovati.length > 0 && (
        <ul className="calendario-elenco-voci">
          {ricerca.trovati.map((p) => (
            <li key={p.id}>
              <Bottone variante="secondario" larghezzaPiena onClick={() => void carica(p)}>{p.etichetta}</Bottone>
            </li>
          ))}
        </ul>
      )}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
    </Modale>
  )
}
